import { Component, viewChild, OnInit, ChangeDetectionStrategy, inject, input, effect, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { CreateCampaignLogicService } from '../../services/create-campaign-logic.service';
import { AbstractControl, AsyncValidatorFn, FormsModule, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatSelectionList, MatListModule } from '@angular/material/list';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, filter, map, Observable, of, startWith, take } from 'rxjs';
import { FacebookHelperService } from '../../services/facebook-helper.service';
import { CreateCampaignApiService } from '../../services/create-campaign-api.service';
import { ApplicationEnum } from 'src/app/models';
import { AiCommentGeneratorComponent } from './components/ai-comment-generator/ai-comment-generator.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'comments-on-post',
    templateUrl: './comments-on-post.component.html',
    styleUrls: ['./comments-on-post.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AsyncPipe, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatListModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, MatTooltipModule],
    providers: [provideNativeDateAdapter()]
})
export class CommentsOnPostComponent implements OnInit {

  // Signal-based inputs
  applicationId = input<string | undefined>(undefined);
  ownerUnitId = input<string | undefined>(undefined);
  regionId = input<string | undefined>(undefined);
  gender = input<string | undefined>(undefined);
  ageFrom = input<number | undefined>(undefined);
  ageTo = input<number | undefined>(undefined);

  private appId$ = toObservable(this.applicationId);
  private ownerUnitId$ = toObservable(this.ownerUnitId);
  private regionId$ = toObservable(this.regionId);
  private gender$ = toObservable(this.gender);
  private ageFrom$ = toObservable(this.ageFrom);
  private ageTo$ = toObservable(this.ageTo);

  constructor() {
    // when the parent passes a new value to any of these inputs,
    // Angular re-executes this block and triggers form revalidation.
    effect(() => {
      this.applicationId();
      this.ownerUnitId();
      this.regionId();
      this.gender();
      this.ageFrom();
      this.ageTo();
      this.engagementFormGroup?.updateValueAndValidity();
    });
  }

  private readonly TIKTOK_VIDEO_URL_REGEX = /tiktok\.com\/@[^/]+\/video\/\d+|vm\.tiktok\.com\//;

  private createCampaignService = inject(CreateCampaignLogicService);
  private facebookHelperService = inject(FacebookHelperService);
  private apiService = inject(CreateCampaignApiService);
  private fb = inject(FormBuilder);
  public dialog = inject(MatDialog);

  get isFacebook(): boolean { return this.applicationId() === ApplicationEnum.facebook; }

  public countAvatarsInGroup$!: Observable<number | null>;

  public engagementFormGroup: FormGroup = new FormGroup({});
  list = viewChild<MatSelectionList>('list');


  ngOnInit(): void { 

    const now = new Date();

    // Format time as HH:mm (e.g., "14:30")
    const currentTime = now.getHours().toString().padStart(2, '0') + ':' + 
                        now.getMinutes().toString().padStart(2, '0');
   
    const isTikTok = this.applicationId() === ApplicationEnum.tiktok;
    const urlRegex = isTikTok
      ? this.TIKTOK_VIDEO_URL_REGEX
      : this.facebookHelperService.COMBINED_FB_POST_REGEX;

    // FromGroup Definition
    this.engagementFormGroup = this.fb.group(
      {
        postUrl: [null, [Validators.required, Validators.pattern(urlRegex)]],
        resourceType: [isTikTok ? 'page' : null],
        groupId: [null],
        intervalSeconds: new FormControl(30, [
          Validators.required, 
          Validators.min(0),
          Validators.pattern("^[0-9]*$") // Ensures only whole integers
        ]),        
        startDate:  [now, Validators.required],
        startTime: [currentTime, Validators.required],
        commentList: [[], Validators.required]
      },
      {
        asyncValidators: [this.avatarsLimitValidator()]
      }
    );
  
    // Create a stream for groupId changes
    const groupIdChanges$ = this.engagementFormGroup.valueChanges.pipe(
      startWith(this.engagementFormGroup.value),
      map(form => form.groupId),
      distinctUntilChanged() // Only trigger if groupId actually changes
    );

    const resourceType$ = this.engagementFormGroup.get('resourceType')!.valueChanges.pipe(
      startWith(this.engagementFormGroup.get('resourceType')!.value),
      distinctUntilChanged()
    );

    // this code retrieves avatars count from DB each time a control or input changes
    // it is used for displaying the avatars limit message
    this.countAvatarsInGroup$ = this.createCampaignService.getAvatarCount$(
      this.appId$,
      this.ownerUnitId$,
      groupIdChanges$,
      resourceType$,
      this.regionId$,
      this.gender$,
      this.ageFrom$,
      this.ageTo$
    );

  }


  ngAfterViewInit() {
    // Log form status changes
    this.engagementFormGroup.statusChanges.subscribe(status => {
      console.log('Form status:', status);
      console.log('Form errors:', this.engagementFormGroup.errors);
      console.log('Form valid:', this.engagementFormGroup.valid);
    });

    // Log individual control status
    Object.keys(this.engagementFormGroup.controls).forEach(key => {
      const control = this.engagementFormGroup.get(key);
      control?.statusChanges.subscribe(status => {
        console.log(`${key} status:`, status, 'errors:', control.errors);
      });
    });
  }

  // Prevents typing non-numeric characters (including 'e', '.', '-', '+')
  onlyNumbers(event: KeyboardEvent) {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  onPostUrlBlur(): void {
    const control = this.engagementFormGroup.get('postUrl');

    if (control?.invalid || !control?.value) {
      this.engagementFormGroup.patchValue({ resourceType: null, groupId: null });
      return;
    }

    if (this.applicationId() === ApplicationEnum.tiktok) {
      this.engagementFormGroup.patchValue({ resourceType: 'page', groupId: null });
      return;
    }

    const result = this.facebookHelperService.parseUrl(control.value);
    this.engagementFormGroup.patchValue(result, { emitEvent: true });
  }


  async onFileUpload(event: Event) {
    const element = event.target as HTMLInputElement;
    const file = element.files?.[0];
    if (!file) return;

    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.getWorksheet(1);
    const data: string[] = [];

    worksheet?.eachRow((row) => {
      const cell = row.getCell(1);
      let val = cell.value;

      // 1. Check if it's Rich Text (which has a 'richText' property)
      if (val && typeof val === 'object' && 'richText' in val) {
        val = val.richText.map(t => t.text).join('');
      }

      // 2. Push the string value
      if (val) {
        data.push(val.toString().trim());
      }
    });

    this.engagementFormGroup.get('commentList')?.setValue(data);
    element.value = '';
  }

  clearList() {
    this.engagementFormGroup.get('commentList')?.setValue([]);
  }

  deleteSelected() {
    const selectedValue = this.list()?.selectedOptions.selected[0]?.value;
    if (selectedValue !== undefined) {
      const currentList = this.engagementFormGroup.get('commentList')?.value as string[];
      const newList = currentList.filter(item => item !== selectedValue);
      this.engagementFormGroup.get('commentList')?.patchValue(newList);
    }
  }

  newItemText = signal('');
  editingIndex = signal<number | null>(null);
  editingText = signal('');
  isGenerating = signal(false);

  addItem() {
    const text = this.newItemText().trim();
    if (!text) return;

    const currentList = this.engagementFormGroup.get('commentList')?.value || [];
    const updatedList = [...currentList, text];

    this.engagementFormGroup.get('commentList')?.setValue(updatedList);
    this.newItemText.set('');
  }

  
  avatarsLimitValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const commentList = control.get('commentList')?.value || [];
      const requiredCount = commentList.length;

      if (requiredCount === 0) return of(null);

      return this.countAvatarsInGroup$.pipe(
        // filter out 'null' so the validator waits for the real server response
        filter(val => val !== null),
        take(1),
        map(availableCount => {
          if (availableCount === -1) {
            return { avatarCountError: true };
          }
          if (availableCount !== null && availableCount < requiredCount) {
            return { insufficientAvatars: { available: availableCount, required: requiredCount } };
          }
          return null;
        })
      );
    };
  }


  onOptionClick(event: MouseEvent, index: number) {
    const target = event.target as HTMLElement;
    const isRadio = target.closest('.mdc-radio') || target.closest('mat-pseudo-checkbox') || target.closest('.mat-mdc-list-option-checkbox-before');
    if (!isRadio) {
      this.list()?.deselectAll();
    }
  }

  editItem(index: number) {
    const list = this.engagementFormGroup.get('commentList')?.value || [];
    this.editingIndex.set(index);
    this.editingText.set(list[index]);
  }

  saveEdit(index: number) {
    if (this.editingIndex() === null) return;
    const trimmed = this.editingText().trim();
    if (trimmed) {
      const list = [...(this.engagementFormGroup.get('commentList')?.value || [])];
      list[index] = trimmed;
      this.engagementFormGroup.get('commentList')?.setValue(list);
    }
    this.editingIndex.set(null);
  }

  cancelEdit() {
    this.editingIndex.set(null);
  }

  openAiCommentGenerator() {
    const dialogRef = this.dialog.open(AiCommentGeneratorComponent, {
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isGenerating.set(true);
        this.apiService.generateCommentsAI(result.prompt, result.commentCount, result.language)
          .subscribe({
            next: response => {
              const comments = Array.isArray(response) ? response : (response as any).comments ?? [];
              const currentList = this.engagementFormGroup.get('commentList')?.value || [];
              this.engagementFormGroup.get('commentList')?.setValue([...currentList, ...comments]);
            },
            complete: () => {
              this.isGenerating.set(false);
            },
            error: () => {
              this.isGenerating.set(false);
            }
          });
      }
    });
  }

}
